<template>
    <VJoyWrapper
        class="project-view-no-actions__wrapper"
        direction="column"
        row-gap="1"
        :data-testid="`project-activity-${activityState?.replaceAll('_', '-').toLowerCase()}`"
    >
        <MaltIllustration v-if="statusInfo" :name="statusInfo.illustration" alt="" width="130" />
        <h2>{{ statusInfo?.titleText }}</h2>
        <p>{{ statusInfo?.messageText }}</p>
        <VJoyButton
            v-if="projectInformation.projectStatus === ProjectStatus.Ended && projectInformation.renewStatus === 'RENEWABLE'"
            variant="main"
            size="small"
            @click="openRenewalDrawer"
        >
            {{ t('projectclient.projectview.main.actions.renew.project') }}
        </VJoyButton>

        <VJoyButton
            v-if="canMarkProjectComplete && projectInformation.contract.type === 'FIXED_PRICE'"
            class="joy-mt-6"
            variant="secondary"
            @click="handleCompleteProject"
        >
            {{ $t('projectclient.projectview.main.actions.complete.project.complete-project') }}
        </VJoyButton>
    </VJoyWrapper>
    <EndProjectOverlay v-if="isEndProjectDialogVisible" @close:dialog="handleShowEndProjectDialog" />
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {useProjectInformationStore, useUserStore} from '@common/stores';
    import {useActivityStore} from '@features/project-view/stores';
    import {EndProjectOverlay} from '@features/project-view/ui/common-layout/project-actions';
    import {navigateTo, useRoute} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {MaltIllustration} from '#components';
    import {useTranslation} from '#imports';
    import type {MaltIllustrationName} from '@malt/nuxt-joy-module/types/malt-illustration-name';
    import {VJoyButton, VJoyWrapper} from '@maltjoy/core-vue';
    import {ProjectStatus} from '@api';
    import {useRenewProject} from '@/features/project-view/composables';

    const userStore = useUserStore();

    const {t} = useTranslation();
    const {roles} = storeToRefs(userStore);

    const {params} = useRoute('project-projectId-index');

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);

    const {openRenewalDrawer} = useRenewProject();

    const activityStore = useActivityStore();
    const {activityState} = storeToRefs(activityStore);

    const isParticipant = computed(() => projectInformation.value?.participant);
    const hasAuthorityToEndProject = computed(() => roles.value.includes('ROLE_MISSION_VALIDATION'));

    const canMarkProjectComplete = computed(() => {
        return (
            !projectInformation.value.completionRequested &&
            projectInformation.value.projectStatus === ProjectStatus.InProgress &&
            (isParticipant.value || hasAuthorityToEndProject.value) &&
            (activityState.value === 'NO_ACTION_YET' || activityState.value === 'NO_MORE_ACTION')
        );
    });

    function handleCompleteProject() {
        if (projectInformation.value.contract.type === 'COST_PLUS') {
            handleShowEndProjectDialog();
        } else {
            navigateTo(`/project/${params.projectId}/validation?origin=project-view`);
        }
    }

    const statusInfo = computed((): {titleText: string; messageText: string; illustration: MaltIllustrationName} | null => {
        switch (activityState.value) {
            case 'NO_ACTION_YET':
                return {
                    titleText: t('projectclient.projectview.activity.empty.state.NO_ACTION_YET.title'),
                    messageText: t('projectclient.projectview.activity.empty.state.NO_ACTION_YET.description', {
                        0: projectInformation.value.freelancer.firstName,
                    }),
                    illustration: 'clapping-hands--high',
                };
            case 'NO_MORE_ACTION':
                return {
                    titleText: t('projectclient.projectview.activity.empty.state.NO_MORE_ACTION.title'),
                    messageText: t('projectclient.projectview.activity.empty.state.NO_MORE_ACTION.description', {
                        0: projectInformation.value.freelancer.firstName,
                    }),
                    illustration: 'clapping-hands--high',
                };
            case 'PENDING_ACTIONS':
                return {
                    titleText: t('projectclient.projectview.activity.empty.state.PENDING_ACTIONS.title'),
                    messageText: t('projectclient.projectview.activity.empty.state.PENDING_ACTIONS.description', {
                        0: projectInformation.value.freelancer.firstName,
                    }),
                    illustration: 'computer-hourglass--high',
                };
            case 'NO_MORE_ACTION_PROJECT_OVER':
                return {
                    titleText: t('projectclient.projectview.activity.empty.state.NO_MORE_ACTION_PROJECT_OVER.title'),
                    messageText: t('projectclient.projectview.activity.empty.state.NO_MORE_ACTION_PROJECT_OVER.description'),
                    illustration: 'trophy--high',
                };
            case 'NO_MORE_ACTION_COMPLETION_REQUESTED':
                return {
                    titleText: t('projectclient.projectview.activity.empty.state.NO_MORE_ACTION_COMPLETION_REQUESTED.title'),
                    messageText: t('projectclient.projectview.activity.empty.state.NO_MORE_ACTION_COMPLETION_REQUESTED.description', {
                        0: projectInformation.value.freelancer.firstName,
                    }),
                    illustration: 'invoices--high',
                };
            default:
                return null;
        }
    });

    const isEndProjectDialogVisible = ref(false);

    function handleShowEndProjectDialog() {
        isEndProjectDialogVisible.value = !isEndProjectDialogVisible.value;
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text';

    .project-view-no-actions {
        &__wrapper {
            flex: 1;
            width: 500px;
            margin-top: var(--joy-core-spacing-12);
            padding: var(--joy-core-spacing-6) var(--joy-core-spacing-2);
            text-align: center;

            & > h2 {
                color: var(--joy-color-neutral-60);
                margin: var(--joy-core-spacing-6) 0 var(--joy-core-spacing-1) 0;

                @include text.getFontBySize(primary-650);
            }

            & > p {
                text-align: center;
            }
        }
    }
</style>
